import { Component, OnInit } from '@angular/core';
import { ColmecWorkingOrderStepComponent } from '../colmec-working-order-step/colmec-working-order-step.component';
import { ColmecWorkOrder } from '../../colmec-working-order-wrapper/colmec-working-order-wrapper.component';
import { ColmecPartnerAgreementsService } from '../../../../services/colmec-partner-agreements.service';
import { first, forkJoin, of, switchMap } from 'rxjs';
import { OptionsListMulti } from 'gung-common';
import { CustomerService, MetadataService } from 'gung-standard';
import { format } from 'date-fns';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'colmec-working-order-summary-step',
    templateUrl: './colmec-working-order-summary-step.component.html',
    styleUrls: ['./colmec-working-order-summary-step.component.scss'],
    standalone: false
})
export class ColmecWorkingOrderSummaryStepComponent extends ColmecWorkingOrderStepComponent implements OnInit {
  public calculatedTotals: { total: number; currency: string };
  public calculatedTotalsTires: { qty: number; total: number; currency: string };
  public calculatedTotalsServices: { qty: number; total: number; currency: string };
  public calculatedTotalsExtras: { qty: number; total: number; currency: string };

  public reasonForWorkOptionsList: OptionsListMulti[] = [];

  public basicInfo: {
    agreementName: string;
    date: string;
    regNr: string;
    reasons: string;
  };

  public getLabel(): string {
    return 'SUMMARY';
  }

  public getBtnTexts(workingOrder: ColmecWorkOrder): { next: string | void; prev: string | void } | void {
    return {
      next: 'SUBMIT_WORKING_ORDER',
      prev: null
    };
  }

  constructor(
    protected agreementService: ColmecPartnerAgreementsService,
    protected metadataService: MetadataService,
    protected customerService: CustomerService,
    protected translateService: TranslateService
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.onNextBtnClicked.subscribe(_ => this.handleNextButtonClicked());
    this.onPreviousBtnClicked.subscribe(_ => this.handlePreviousButtonClicked());
    this.calculateAllTotals();
    this.getReasonForWorkData();

    this.agreementService
      .getPartnerAgreementById(this.workingOrder.agreementId)
      .pipe(first())
      .subscribe(pa => {
        let name: string = '';

        name += `${pa.customerName} (${pa.customerId}) - `;

        name += `${pa.partnerName} (${pa.partnerId})`;

        this.basicInfo = {
          agreementName: name,
          date: format(new Date(this.workingOrder.workPerformedDate), 'yyyy-MM-dd'),
          regNr: this.workingOrder.regNr,
          reasons: this.reasonForWorkOptionsList
            .filter(r => this.workingOrder.reasonForWork.includes(r.id))
            .map(r => r.name)
            .join(', ')
        };
      });
  }

  private handleNextButtonClicked(): void {
    this.stepDone.emit(this.workingOrder);
  }

  private handlePreviousButtonClicked(): void {
    this.stepPrevious.emit(this.workingOrder);
  }

  protected calculateAllTotals(): void {
    const tires: { qty: number; total: number; currency: string } = { qty: 0, total: 0, currency: '' };
    for (const row of this.workingOrder.tires) {
      tires.qty += row.qty;
      tires.total += row.price.value * row.qty;
      tires.currency = row.price.currency;
    }
    this.calculatedTotalsTires = tires;

    const services: { qty: number; total: number; currency: string } = { qty: 0, total: 0, currency: '' };
    for (const row of this.workingOrder.services) {
      services.qty += row.qty;
      services.total += row.price.value * row.qty;
      services.currency = row.price.currency;
    }
    this.calculatedTotalsServices = services;

    const extras: { qty: number; total: number; currency: string } = { qty: 0, total: 0, currency: '' };
    for (const row of this.workingOrder.extras) {
      extras.qty += row.extra?.qty || 1;
      extras.total += Number(row.price) * (row.extra?.qty || 1);
      extras.currency = tires.currency;
    }
    this.calculatedTotalsExtras = extras;

    this.calculatedTotals = {
      total: tires.total + services.total + extras.total,
      currency: tires.currency
    };
  }

  private getReasonForWorkData(): void {
    const workingOrderReasonsMetadata = this.metadataService.getMetadataTableList('WORKING_ORDER_REASONS');

    const reasons: OptionsListMulti[] = workingOrderReasonsMetadata.map(r => {
      return {
        id: r.id,
        name: this.translateService.instant(r.name || ''),
        selected: false
      };
    });

    for (const option of reasons) {
      if (this.workingOrder.reasonForWork.includes(option.id)) {
        option.selected = true;
      }
    }

    this.reasonForWorkOptionsList = reasons;
  }
}
